/*
 * Copyright 2022 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {
  DocsIcon,
  EmptyState,
  InfoCardVariants,
  Table,
  TableColumn,
} from '@backstage/core-components';
import {
  EntityRefLinks,
  getEntityRelations,
  useEntity,
} from '@backstage/plugin-catalog-react';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  IconButton,
  Link,
  Tooltip,
  Typography,
} from '@material-ui/core';

import { useInternalIdData } from '../../catalog/CustomAboutCard/hooks';
import { EditAccountTagsIcon } from './EditAccountTagsIcon';
import { useStyles } from '../styles';
import { RELATION_OWNED_BY } from '@backstage/catalog-model';

/** @public */
export interface EnterpriseTagsCardProps {
  variant?: InfoCardVariants;
}

export const EnterpriseTagsCard = (props: EnterpriseTagsCardProps) => {
  const { entity } = useEntity();
  const { variant } = props;
  const classes = useStyles();
  const internalId = useInternalIdData(
    entity?.metadata?.labels?.['pfg-app-inventory-id'] || '',
  );

  let cardClass = '';
  let cardContentClass = '';
  if (variant === 'gridItem') {
    cardClass = classes.gridItemCard;
    cardContentClass = classes.gridItemCardContent;
  } else if (variant === 'fullHeight') {
    cardClass = classes.fullHeightCard;
    cardContentClass = classes.fullHeightCardContent;
  }

  const ownedByRelations = getEntityRelations(entity, RELATION_OWNED_BY);

  const columns: TableColumn<{ key: string; value: string }>[] = [
    {
      render: row => {
        return <Typography variant="body2">{row.key}</Typography>;
      },
    },
    {
      render: row => {
        if (row.key === 'pfg-app-inventory-id' && internalId) {
          if (internalId === 'Not Found') {
            return <Typography variant="body2">{row.value}</Typography>;
          }
          return (
            <Link
              href={`https://principal.leanix.net/PrincipalProduction/factsheet/Application/${internalId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {row.value}
            </Link>
          );
        }
        if (row.key === 'pfg-owner') {
          return (
            <>
              {ownedByRelations.length > 0 && (
                <EntityRefLinks
                  entityRefs={ownedByRelations}
                  defaultKind="group"
                />
              )}
            </>
          );
        }
        return <Typography variant="body2">{row.value}</Typography>;
      },
    },
  ];

  const labels = entity?.metadata?.labels || {};
  const pfgOrgUnit = String(entity?.spec?.['pfg-org-unit'] || '');
  const pfgOwner = String(entity?.spec?.['pfg-owner'] || '');

  // Filter labels to only include those starting with "pfg" and sort alphabetically
  const pfgLabels = Object.fromEntries(
    Object.entries(labels)
      .filter(([key]) => key.toLowerCase().startsWith('pfg'))
      .sort(([a], [b]) => a.localeCompare(b)),
  );

  const formatOrgUnitDisplay = (input: string): string => {
    return input
      .split('.')
      .map(segment =>
        segment
          .split('-')
          .map(
            word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
          )
          .join(' '),
      )
      .join(' / ');
  };

  // Enterprise tags record - only PFG tags
  const enterpriseTags: Record<string, string> = {
    ...pfgLabels,
    ...(pfgOrgUnit && { 'pfg-org-unit': formatOrgUnitDisplay(pfgOrgUnit) }),
    ...(pfgOwner && { 'pfg-owner': pfgOwner }),
  };

  const docs = (
    <>
      <CardActions>
        <Tooltip title="Read about required, optional, and custom tags">
          <IconButton
            href="/docs/principalfinancialgroup-emu/component/principal-engineering-handbook/infrastructure/aws/management/tagging-standards/#enterprise-standard-tags"
            target="_blank"
          >
            <DocsIcon />
          </IconButton>
        </Tooltip>
        {entity.metadata?.tags?.includes('aws') && <EditAccountTagsIcon />}
      </CardActions>
    </>
  );

  return (
    <Card className={cardClass}>
      <CardHeader title="Enterprise Tags" action={docs} />
      <Divider />
      <CardContent className={cardContentClass}>
        {Object.keys(enterpriseTags).length === 0 ? (
          <EmptyState
            title="No Enterprise Tags Found"
            missing="info"
            description="No tags defined on this account. Please open a ticket with the Cloud Platform Engineering team."
            action={
              <Button
                variant="contained"
                color="primary"
                href="https://principal.service-now.com/esc?id=sc_cat_item&sys_id=5a40f5f81b8b69d023b3fea9cc4bcb05"
              >
                Open Ticket
              </Button>
            }
          />
        ) : (
          <Table
            columns={columns}
            data={Object.entries(enterpriseTags).map(([key, value]) => ({
              key,
              value,
            }))}
            options={{
              search: false,
              showTitle: true,
              loadingType: 'linear',
              header: false,
              padding: 'dense',
              pageSize: 10,
              toolbar: false,
              paging: Object.keys(enterpriseTags).length > 10,
            }}
          />
        )}
      </CardContent>
    </Card>
  );
};
